import React, { useMemo } from "react";
import { Button } from "@shared/ui/button";
import { useUtilityServices } from "@features/cx/consumerOnboarding/hooks";

interface ServiceDetailsReviewProps {
  data?: any;
  onEdit?: () => void;
}

export function ServiceDetailsReview({
  data,
  onEdit,
}: ServiceDetailsReviewProps) {
  const { data: utilityServicesData } = useUtilityServices();
  const utilityServicesList = useMemo(() => {
    const list = Array.isArray(utilityServicesData)
      ? utilityServicesData
      : utilityServicesData?.result || [];
    return Array.isArray(list) ? list : [];
  }, [utilityServicesData]);

  const checkIsMetered = (serviceName: string) => {
    if (!utilityServicesList || utilityServicesList.length === 0) {
      if (
        data?.number_of_connections?.[serviceName] !== undefined ||
        data?.service_quantities?.[serviceName] !== undefined
      ) {
        return true;
      }
      return true;
    }
    const match = utilityServicesList.find(
      (u: any) =>
        String(u.name || "")
          .trim()
          .toLowerCase() ===
          String(serviceName || "")
            .trim()
            .toLowerCase() ||
        String(u.label || "")
          .trim()
          .toLowerCase() ===
          String(serviceName || "")
            .trim()
            .toLowerCase(),
    );
    if (!match) {
      return !!(
        data?.number_of_connections?.[serviceName] ||
        data?.service_quantities?.[serviceName]
      );
    }
    const val =
      match.isMeterRequired ??
      match.is_meter_required ??
      match.isMetered ??
      match.is_metered ??
      match.metered ??
      match.isMeter ??
      match.is_meter;
    return val === true || val === 1 || val === "true" || val === "1";
  };

  const serviceStartDate =
    data?.service_start_date ||
    data?.serviceStartDate ||
    data?.registration_date ||
    data?.primary_consumer?.registration_date ||
    data?.created_date ||
    data?.createdDate ||
    "Not specified";

  const rawContactMethods =
    data?.preferred_contact_methods ||
    data?.preferredContactMethods ||
    data?.preferred_contact_method ||
    data?.primary_consumer?.preferred_contact_method ||
    [];
  const contactMethodsArray = Array.isArray(rawContactMethods)
    ? rawContactMethods
    : typeof rawContactMethods === "string"
      ? [rawContactMethods]
      : [];

  const rawServices =
    data?.selectServicesHeader ||
    data?.selected_services ||
    (data?.number_of_connections
      ? Object.keys(data.number_of_connections)
      : null) ||
    (data?.service_quantities ? Object.keys(data.service_quantities) : null) ||
    data?.utilityService ||
    data?.utility_service ||
    data?.primary_consumer?.utility_service ||
    data?.utility_services ||
    [];

  let selectedServicesArray: Array<{ label: string; count: any }> = [];

  if (Array.isArray(rawServices)) {
    selectedServicesArray = rawServices
      .map((service: any) => {
        const srvName =
          typeof service === "object" && service !== null
            ? service.value || service.label
            : service;
        const label =
          typeof service === "object" && service !== null
            ? service.label || service.value
            : service;
        const count =
          data?.number_of_connections?.[srvName] ??
          data?.service_quantities?.[srvName] ??
          data?.[`service_quantity_${srvName}`] ??
          data?.number_of_connections?.[label] ??
          data?.service_quantities?.[label] ??
          data?.[`service_quantity_${label}`] ??
          0;
        return {
          label: String(label),
          srvName: String(srvName),
          count: Number(count) || 0,
        };
      })
      .filter(
        (item) => checkIsMetered(item.srvName || item.label) && item.count > 0,
      );
  } else if (rawServices && typeof rawServices === "object") {
    selectedServicesArray = Object.entries(rawServices)
      .map(([name, qty]) => {
        const count =
          typeof qty === "object" && qty !== null
            ? (qty as any).numberOfConnection || (qty as any).count || 0
            : Number(qty) || 0;
        return { label: name, srvName: name, count };
      })
      .filter(
        (item) => checkIsMetered(item.srvName || item.label) && item.count > 0,
      );
  }


  const isPaperless =
    data?.paperless_billing === true ||
    data?.paperlessBilling === true ||
    data?.is_e_bill === "True" ||
    data?.is_e_bill === true ||
    data?.isEBill === "True" ||
    data?.isEBill === true ||
    data?.is_paperless === true;

  const rawPropertyType =
    data?.property_type ||
    data?.propertyType ||
    data?.ownership_details?.structure_choice ||
    data?.ownershipDetails?.structureChoice ||
    data?.structure_choice ||
    "";

  const propertyTypeNormalized = String(rawPropertyType).toLowerCase();

  const propertyTypeDisplay = (() => {
    if (propertyTypeNormalized.includes("individual"))
      return "A standalone property (Individual Account)";
    if (propertyTypeNormalized.includes("tenant"))
      return "A rented property - I am a tenant (Sub Account)";
    if (
      propertyTypeNormalized.includes("business") ||
      propertyTypeNormalized.includes("master")
    )
      return "Multi-location business or group";
    return rawPropertyType || "Not specified";
  })();

  const ownerDetails =
    data?.ownerDetails ||
    data?.ownership_details?.ownership_detail ||
    data?.ownershipDetails?.ownershipDetail ||
    {};

  const isTenant =
    propertyTypeNormalized.includes("tenant") ||
    !!ownerDetails.name ||
    !!ownerDetails.owner_name;

  const businessAccountLink =
    data?.business_account_link ||
    (propertyTypeNormalized.includes("sub_account")
      ? "existing"
      : propertyTypeNormalized.includes("master")
        ? "new"
        : "");

  const existingBusinessAccount = data?.existing_business_account || {
    account_number:
      data?.ownership_details?.master_account_no ||
      data?.ownershipDetails?.masterAccountNo ||
      "",
    account_email:
      data?.ownership_details?.master_account_name ||
      data?.ownershipDetails?.masterAccountName ||
      "",
  };

  const isBusiness =
    propertyTypeNormalized.includes("business") ||
    propertyTypeNormalized.includes("master") ||
    propertyTypeNormalized.includes("sub_account") ||
    !!businessAccountLink;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Service Details</h2>
        <Button variant="outline" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Service Start Date */}
        <div>
          <p className="text-sm text-gray-600 mb-1">Service Start Date</p>
          <p className="text-base text-gray-900 font-medium">
            {serviceStartDate}
          </p>
        </div>

        {/* Preferred Contact Method */}
        <div>
          <p className="text-sm text-gray-600 mb-1">Preferred Contact Method</p>
          <p className="text-base text-gray-900 font-medium">
            {contactMethodsArray.length > 0
              ? contactMethodsArray.join(", ")
              : "Not specified"}
          </p>
        </div>

        {/* Selected Services & Connections */}
        <div className="col-span-1 md:col-span-2">
          <p className="text-sm text-gray-600 mb-2">
            Selected Services & Connections
          </p>
          {selectedServicesArray.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {selectedServicesArray.map(({ label, count }) => (
                <div
                  key={String(label)}
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg shadow-2xs hover:border-blue-300 transition-all"
                >
                  <span
                    className="text-sm font-medium text-gray-800 truncate pr-2"
                    title={String(label)}
                  >
                    {label}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 shrink-0">
                    {count} {Number(count) === 1 ? "unit" : "units"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-base text-gray-500 italic">None selected</p>
          )}
        </div>

        <div>
          <p className="text-sm text-gray-600 mb-1">
            Subscribed for Paperless Billing
          </p>
          <p className="text-base text-gray-900 font-medium">
            {isPaperless ? "Yes" : "No"}
          </p>
        </div>

        {/* Property Type / What best describes this connection? */}
        <div className="col-span-1 md:col-span-2">
          <p className="text-sm text-gray-600 mb-1">
            What best describes this connection?
          </p>
          <p className="text-base text-gray-900 font-medium">
            {propertyTypeDisplay}
          </p>
        </div>

        {isTenant && (
          <div className="col-span-1 md:col-span-2">
            <hr className="mb-6 border-gray-200" />
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Owner Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Owner Name</p>
                <p className="text-base text-gray-900 font-medium">
                  {ownerDetails?.name ||
                    ownerDetails?.owner_name ||
                    "Not specified"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Owner Phone</p>
                <p className="text-base text-gray-900 font-medium">
                  {ownerDetails?.phone ||
                    ownerDetails?.owner_phone ||
                    "Not specified"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Owner Email</p>
                <p className="text-base text-gray-900 font-medium">
                  {ownerDetails?.email ||
                    ownerDetails?.owner_email ||
                    "Not specified"}
                </p>
              </div>
              <div className="col-span-1 md:col-span-2">
                <p className="text-sm text-gray-600 mb-1">Owner Address</p>
                <p className="text-base text-gray-900 font-medium">
                  {ownerDetails?.address ||
                    ownerDetails?.owner_address ||
                    "Not specified"}
                </p>
              </div>
            </div>
          </div>
        )}

        {isBusiness && (
          <div className="col-span-1 md:col-span-2">
            <hr className="mb-6 border-gray-200" />
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Business Account Linking
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1 md:col-span-2">
                <p className="text-sm text-gray-600 mb-1">
                  Link to Existing Business Account
                </p>
                <p className="text-base text-gray-900 font-medium">
                  {businessAccountLink === "existing"
                    ? "Yes, link to an existing business account"
                    : "No, create this as a new main business account"}
                </p>
              </div>
            </div>

            {businessAccountLink === "existing" && (
              <>
                <hr className="my-6 border-gray-200" />
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Existing Business Account Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      Business Account Number
                    </p>
                    <p className="text-base text-gray-900 font-medium">
                      {existingBusinessAccount?.account_number ||
                        "Not specified"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      Business Account Email
                    </p>
                    <p className="text-base text-gray-900 font-medium">
                      {existingBusinessAccount?.account_email ||
                        "Not specified"}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
